/**
 * MP3 encoding via @breezystack/lamejs (ESM fork of lamejs) — dynamically
 * imported so the ~300 KB encoder stays out of the main bundle until a
 * bounce is requested.
 */
import type { Mp3Encoder } from '@breezystack/lamejs';

let pending: Promise<typeof import('@breezystack/lamejs')> | null = null;

async function getLame(): Promise<typeof import('@breezystack/lamejs')> {
  if (!pending) pending = import('@breezystack/lamejs');
  return pending;
}

/**
 * Encode channel data to MP3. Returns the raw MP3 bytes.
 * `channels` = 1 or 2 Float32Array tracks.
 */
export async function encodeMp3(
  channels: Float32Array[],
  sampleRate: number,
  kbps = 192,
): Promise<Uint8Array<ArrayBuffer>> {
  const lame = await getLame();
  const numCh = Math.min(2, channels.length);
  const len = channels[0].length;
  const enc: Mp3Encoder = new lame.Mp3Encoder(numCh, sampleRate, kbps);

  const left = new Int16Array(len);
  const right = new Int16Array(len);
  for (let i = 0; i < len; i++) {
    left[i] = toInt16(channels[0][i]);
    right[i] = numCh === 2 ? toInt16(channels[1][i]) : left[i];
  }

  const block = 1152;
  const out: Uint8Array[] = [];
  for (let i = 0; i < len; i += block) {
    const l = left.subarray(i, i + block);
    const r = right.subarray(i, i + block);
    const mp3 = numCh === 2 ? enc.encodeBuffer(l, r) : enc.encodeBuffer(l);
    if (mp3.length > 0) out.push(mp3);
  }
  const tail = enc.flush();
  if (tail.length > 0) out.push(tail);

  const total = out.reduce((n, c) => n + c.length, 0);
  const result = new Uint8Array(total);
  let off = 0;
  for (const c of out) {
    result.set(c, off);
    off += c.length;
  }
  return result;
}

function toInt16(v: number): number {
  const s = Math.max(-1, Math.min(1, v));
  return s < 0 ? s * 0x8000 : s * 0x7fff;
}